import { Action, TimeString, toTimeString } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { SettingItemScreenProps } from 'home/stack/types'
import moment from 'moment'
import React, { useCallback, useLayoutEffect, useState } from 'react'
import { usePerformSettingAction } from 'settings/queries/perform_action'
import { useStorePreferences } from 'settings/queries/preferences'

import { useSettings } from 'settings/queries/settings'
import { SingleSelectSettingView } from 'settings/screens/types/single_select/view'
import {
  getSettingsAction,
  getSettngsSource,
  onMultiSelectValue,
  onSingleSelectValue,
} from 'settings/utils'
import { NotFoundComponent } from 'shared/components/NotFoundComponent'
import { StatusBoundary } from 'shared/components/status_boundary'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useRecentStoresContext } from 'stores/context'
import { DollarsSettingView } from '../types/dollars/view'
import { MultiSelectSettingView } from '../types/multi_select/view'
import { NumberSettingView } from '../types/number/view'
import { TextSettingView } from '../types/text/view'
import { TimeRangeSettingView } from '../types/time_range/view'

export function SettingItemScreen({
  route,
  navigation,
}: SettingItemScreenProps) {
  const { theme } = useTheme()
  const { activeStoreId, homeStoreId } = useRecentStoresContext()
  const { type, id } = route.params

  const settingsQuery = useSettings(type, homeStoreId)
  const isPreferenceType = type.includes('preferences')

  const preferencesQuery = useStorePreferences(activeStoreId, isPreferenceType)

  const mutation = usePerformSettingAction(
    getSettngsSource(type, isPreferenceType ? activeStoreId : homeStoreId)
  )

  const query = isPreferenceType ? preferencesQuery : settingsQuery
  const setting = query.data?.settings.find(s => s.id === id)

  const [startTime, setStartTime] = useState<TimeString>(
    setting?.type === 'timeRange' ? setting.startTime : toTimeString(moment())
  )
  const [endTime, setEndTime] = useState<TimeString>(
    setting?.type === 'timeRange' ? setting.endTime : toTimeString(moment())
  )
  const [inputText, setInputText] = useState(
    setting?.type === 'text'
      ? setting.text
      : setting?.type === 'dollars'
      ? setting.text.replace('$ ', '')
      : ''
  )
  const [error, setError] = useState(undefined)
  const [inputNumber, setInputNumber] = useState(
    setting?.type === 'number' ? setting.number : ''
  )

  const onSave = useCallback(async () => {
    let action: Action | undefined
    switch (setting?.type) {
      case 'timeRange':
        action = getSettingsAction(setting, [`${startTime}-${endTime}`])
        break
      case 'number':
        action = getSettingsAction(setting, [inputNumber])
        break
      case 'dollars':
      case 'text':
        action = getSettingsAction(setting, [inputText])
        break
    }
    if (action) {
      try {
        await mutation.mutateAsync(action)
        navigation.goBack()
      } catch (err: any) {
        setError(err)
      }
    }
  }, [
    endTime,
    inputNumber,
    inputText,
    mutation,
    navigation,
    setting,
    startTime,
  ])

  useLayoutEffect(
    () =>
      setting
        ? showHeaderTitle(navigation, setting.name)
        : showHeaderTitle(navigation, 'Not Found'),
    [navigation, setting]
  )

  useLayoutEffect(() => {
    switch (setting?.type) {
      case 'timeRange':
      case 'text':
      case 'dollars':
      case 'number':
        showHeaderButton({
          navigation,
          theme,
          title: t('save'),
          onPress: onSave,
          position: 'right',
          disabled:
            mutation.isPending ||
            (setting.type !== 'timeRange' && !inputText && !inputNumber),
        })
    }
  })

  function getSettingItemView(): React.JSX.Element {
    switch (setting?.type) {
      case 'number':
        return (
          <NumberSettingView
            number={inputNumber}
            name={setting.name}
            onChangeNumber={setInputNumber}
            error={error}
          />
        )
      case 'dollars':
        return (
          <DollarsSettingView
            text={inputText}
            name={setting.name}
            onChangeText={setInputText}
            error={error}
          />
        )
      case 'timeRange':
        return (
          <TimeRangeSettingView
            startTime={startTime}
            endTime={endTime}
            description={setting.description}
            onStartTimeChange={setStartTime}
            onEndTimeChange={setEndTime}
          />
        )
      case 'text':
        return (
          <TextSettingView
            text={inputText}
            name={setting?.name}
            onChangeText={setInputText}
            error={error}
          />
        )
      case 'singleSelect':
        return (
          <SingleSelectSettingView
            selectedId={setting.selectedId}
            values={setting.values}
            onSelectValue={value =>
              onSingleSelectValue(setting, value, mutation)
            }
          />
        )
      case 'multiSelect':
        return (
          <MultiSelectSettingView
            selectedIds={setting.selectedIds}
            values={setting.values}
            onSelectValue={newSelectedIds =>
              onMultiSelectValue(setting, newSelectedIds, mutation)
            }
          />
        )
      default:
        return <NotFoundComponent title="Setting Not Found" />
    }
  }

  return (
    <StatusBoundary
      loadingMessage={t('saving')}
      isLoading={mutation.isPending}
      error={preferencesQuery.error || settingsQuery.error || mutation.error}
    >
      {getSettingItemView()}
    </StatusBoundary>
  )
}
